import { defineStore } from 'pinia'
import { createSyncedRows, type RemoteSettings } from '../services/syncedRows'
import { IGNORES_PER_PAGE } from '../utils/editorModeTuning'

// How far each page has got through each revision pass, and the habit marks
// dismissed on it. One small row per page, because the settings channel
// re-sends every row on every pull; the text itself is never stored, only a
// few words to find the reviewed-up-to point again and a hash sketch of the
// paragraphs for "changed since".

export interface ReviewPoint {
  /** Index of the paragraph among the page's prose paragraphs. */
  index: number
  /** The paragraph's first words, to find it again if paragraphs move. */
  lead: string
}

export interface PassProgress {
  upTo: ReviewPoint | null
  /** When the pass was finished; null while under way. */
  done: number | null
  /** Paragraph sketch when finished, refreshed by edits made in Editor Mode. */
  sketch: number[] | null
  /** Time of the "Before … pass" snapshot taken at this run's first edit. */
  snapshot: number | null
}

export interface PageRevision {
  passes: Record<string, PassProgress>
  ignores: string[]
}

const LEAD_MAX = 60
const EMPTY_PROGRESS: PassProgress = { upTo: null, done: null, sketch: null, snapshot: null }

const time = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : null)

function parseProgress(raw: any): PassProgress | null {
  if (!raw || typeof raw !== 'object') return null
  const upTo = raw.upTo && Number.isInteger(raw.upTo.index) && raw.upTo.index >= 0
    ? { index: raw.upTo.index, lead: typeof raw.upTo.lead === 'string' ? raw.upTo.lead.slice(0, LEAD_MAX) : '' }
    : null
  return {
    upTo,
    done: time(raw.done),
    sketch: Array.isArray(raw.sketch) ? raw.sketch.filter((n: unknown) => typeof n === 'number').slice(0, 64) : null,
    snapshot: time(raw.snapshot),
  }
}

function parsePage(raw: any): PageRevision | null {
  if (!raw || typeof raw !== 'object') return null
  const passes: Record<string, PassProgress> = {}
  if (raw.passes && typeof raw.passes === 'object') {
    for (const [id, value] of Object.entries(raw.passes)) {
      const progress = parseProgress(value)
      if (progress && id.length <= 40) passes[id] = progress
    }
  }
  const ignores = Array.isArray(raw.ignores)
    ? raw.ignores.filter((k: unknown) => typeof k === 'string' && k.length <= 120).slice(-IGNORES_PER_PAGE)
    : []
  return { passes, ignores }
}

export const useRevisionStore = defineStore('revision', () => {
  const rows = createSyncedRows<PageRevision>({
    storageKey: 'ink-revision',
    remotePrefix: 'revision.page.',
    parse: parsePage,
    pushDelayMs: 5_000,
  })
  const pages = rows.values

  function progress(pageId: string | null, passId: string): PassProgress {
    return (pageId && pages.value[pageId]?.passes[passId]) || EMPTY_PROGRESS
  }

  function update(pageId: string, passId: string, patch: Partial<PassProgress>) {
    const page = pages.value[pageId] ?? { passes: {}, ignores: [] }
    const next = { ...(page.passes[passId] ?? EMPTY_PROGRESS), ...patch }
    if (JSON.stringify(next) === JSON.stringify(page.passes[passId] ?? EMPTY_PROGRESS)) return
    rows.write(pageId, { ...page, passes: { ...page.passes, [passId]: next } })
  }

  function setUpTo(pageId: string, passId: string, upTo: ReviewPoint | null) {
    update(pageId, passId, { upTo: upTo ? { index: upTo.index, lead: upTo.lead.slice(0, LEAD_MAX) } : null })
  }

  function markDone(pageId: string, passId: string, sketch: number[]) {
    update(pageId, passId, { done: Date.now(), sketch })
  }

  /** Back to under way, keeping the reviewed-up-to point. */
  function reopen(pageId: string, passId: string) {
    update(pageId, passId, { done: null, sketch: null })
  }

  /** Starts the pass over on this page. */
  function restart(pageId: string, passId: string) {
    update(pageId, passId, { ...EMPTY_PROGRESS })
  }

  function setSnapshot(pageId: string, passId: string, snapshot: number | null) {
    update(pageId, passId, { snapshot })
  }

  /**
   * Edits made in Editor Mode are revision, so finished passes take them in
   * rather than going stale; passes already showing "changed since" keep it.
   */
  function absorbEdits(pageId: string, sketch: number[], except: Set<string>) {
    const page = pages.value[pageId]
    if (!page) return
    let changed = false
    const passes = { ...page.passes }
    for (const [id, entry] of Object.entries(passes)) {
      if (entry.done && !except.has(id) && JSON.stringify(entry.sketch) !== JSON.stringify(sketch)) {
        passes[id] = { ...entry, sketch }
        changed = true
      }
    }
    if (changed) rows.write(pageId, { ...page, passes })
  }

  function ignoresOf(pageId: string | null): string[] {
    return (pageId && pages.value[pageId]?.ignores) || []
  }

  function addIgnores(pageId: string, keys: string[]) {
    const page = pages.value[pageId] ?? { passes: {}, ignores: [] }
    const fresh = keys.filter((k) => !page.ignores.includes(k))
    if (!fresh.length) return
    rows.write(pageId, { ...page, ignores: [...page.ignores, ...fresh].slice(-IGNORES_PER_PAGE) })
  }

  function clearIgnores(pageId: string) {
    const page = pages.value[pageId]
    if (page?.ignores.length) rows.write(pageId, { ...page, ignores: [] })
  }

  return {
    pages,
    progress,
    setUpTo,
    markDone,
    reopen,
    restart,
    setSnapshot,
    absorbEdits,
    ignoresOf,
    addIgnores,
    clearIgnores,
    syncWithRemote: (remote: RemoteSettings) => rows.syncWithRemote(remote),
  }
})
